import { useEffect, useRef } from "react";

import { useToast } from "@/ds";
import { useMediaQuery } from "@/hooks/use-media-query";
import { getPersistedUser } from "@/modules/auth";
import {
  useFileExport,
  XLSX_LAYOUT_DETAILS,
  type ExportFormat,
  type FileExportOptions,
} from "@/modules/export";
import { exportFichaFinanceira } from "@/modules/financeiro-fichas";
import type { FichaFinanceiraDetalhe } from "@/modules/financeiro-fichas";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { useSecoesScroll } from "@/shared/hooks";
import { MOBILE_QUERY } from "../../../financeiro-obras.constants";
import { useFichaFinanceira } from "../../hooks/use-fichas-financeiras";
import { buildFichaMailto, buildFichaTitulo, openMailto } from "./ficha-mailto";
import {
  FICHA_EXPORT_ERRO,
  FICHA_SALVA_LIFE,
  FICHA_SALVA_MESSAGE,
  FICHA_EXPORT_SUCESSO,
  FICHA_SALVAR_ERRO_LIFE,
} from "./financeiro-ficha-viewer.constants";
import { useGerarFicha } from "../../hooks/use-gerar-ficha";
import { useResponsaveisOpcoes } from "../../hooks/use-responsaveis-opcoes";
import { buildFichaSections, fichaDaPrevia } from "./financeiro-ficha-viewer.utils";
import type { FichaEdicao } from "./financeiro-ficha-viewer.utils";
import type { FinanceiroFichaViewerProps } from "./types-financeiro-ficha-viewer";

interface EscolherFichaEntrada {
  emGeracao: boolean;
  salva: boolean;
  consultada: FichaFinanceiraDetalhe | null;
  fichaDePrevia: ReturnType<typeof fichaDaPrevia> | null;
}

/** Consulta mostra a ficha consultada; geração mostra a prévia, e salva a ficha gravada assim que consultada. */
export function escolherFicha({
  emGeracao,
  salva,
  consultada,
  fichaDePrevia,
}: EscolherFichaEntrada) {
  if (!emGeracao) return consultada;
  if (salva) return consultada ?? fichaDePrevia;
  return fichaDePrevia;
}

/**
 * Ficha financeira com menu lateral que rola o corpo até a seção e acompanha a seção visível.
 * Com `fichaId`, consulta somente leitura (HU 2679); sem ele, geração (HU 2684): prévia com campos
 * editáveis, Cancelar/Salvar e, depois de salvar, a mesma ficha somente leitura.
 */
export function useFinanceiroFichaViewer({
  obraId,
  fichaId,
  onSalva,
  onFechar,
  onPreviaBloqueada,
}: FinanceiroFichaViewerProps) {
  const emGeracao = fichaId === undefined;
  const geracao = useGerarFicha(obraId, emGeracao);
  // Salva a ficha, o viewer deixa a prévia e consulta a ficha gravada, como se tivesse sido aberta.
  const idConsulta = fichaId ?? geracao.salva?.id ?? "";
  const consulta = useFichaFinanceira(obraId, idConsulta, idConsulta !== "");
  const responsaveis = useResponsaveisOpcoes(
    geracao.previa?.obra.inicio ?? null,
    geracao.previa?.obra.fim ?? null,
  );
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const toast = useToast();
  const {
    bodyRef,
    activeId,
    selecionar: selecionarSecao,
    propsCorpo,
  } = useSecoesScroll("identificacao");
  const { status, errorMessage, reload } = emGeracao ? geracao : consulta;
  const salva = geracao.salva !== null;
  const emPrevia = emGeracao && !salva;
  const fichaDePrevia = geracao.previa
    ? fichaDaPrevia(
        obraId,
        geracao.previa,
        geracao.values,
        geracao.salva,
        getPersistedUser()?.name ?? "",
      )
    : null;
  const ficha = escolherFicha({ emGeracao, salva, consultada: consulta.data, fichaDePrevia });
  const data = consulta.data;
  // Exportar (modal de formatos) e E-mail (sempre PDF) compartilham o download; o destino do
  // sucesso é decidido por esta intenção.
  const intencao = useRef<"exportar" | "email">("exportar");
  const { hasPermissao } = usePermissoes();
  const podeExportar = hasPermissao(HUB_PERMISSOES.FINANCEIRO_FICHAS_EXPORTACAO);
  const titulo = buildFichaTitulo(data?.obra.codigo ?? "", data?.obra.embarcacao ?? "");

  const download = useFileExport({
    fileName: titulo,
    loadFile: (format, options) =>
      exportFichaFinanceira(obraId, idConsulta, format, { xlsxLayout: options?.xlsxLayout }),
    onEmpty: () => undefined,
    onSuccess: () => {
      if (intencao.current === "email" && data) {
        openMailto(
          buildFichaMailto({
            codigo: data.obra.codigo,
            embarcacao: data.obra.embarcacao,
            usuario: getPersistedUser()?.name ?? "",
          }),
        );
        return;
      }

      toast.show({
        severity: "success",
        summary: FICHA_EXPORT_SUCESSO,
        life: 3000,
      });
    },
    onError: () =>
      toast.show({
        severity: "error",
        summary: FICHA_EXPORT_ERRO,
        life: 5000,
      }),
  });

  function enviarEmail() {
    if (download.isExporting || !data) return;

    intencao.current = "email";
    void download.run("pdf");
  }

  function exportarArquivo(format: ExportFormat, options?: FileExportOptions) {
    if (download.isExporting || !data) return;

    intencao.current = "exportar";
    void download.run(format, options);
  }

  function abrirExportacao() {
    intencao.current = "exportar";
    download.open();
  }

  // 422 da prévia (MS006): quem abriu o viewer mostra o aviso e volta à listagem.
  const previaBloqueio = emGeracao ? geracao.previaBloqueio : null;
  const aoBloquearPrevia = useRef(onPreviaBloqueada);
  useEffect(() => {
    aoBloquearPrevia.current = onPreviaBloqueada;
  });
  useEffect(() => {
    if (previaBloqueio) aoBloquearPrevia.current?.(previaBloqueio);
  }, [previaBloqueio]);

  // Falha ao gravar (inclui o 422 com a mensagem do core): o formulário segue aberto com o aviso.
  const erroSalvar = geracao.erroSalvar;
  useEffect(() => {
    if (!erroSalvar) return;
    toast.show({ severity: "error", summary: erroSalvar, life: FICHA_SALVAR_ERRO_LIFE });
  }, [erroSalvar, toast]);

  const edicao: FichaEdicao | undefined = emPrevia
    ? {
        values: geracao.values,
        errors: geracao.errors,
        alterar: geracao.alterar,
        responsaveis,
      }
    : undefined;
  const sections = ficha ? buildFichaSections(ficha, edicao) : [];

  async function salvar() {
    const resultado = await geracao.salvar();

    if (resultado === "salva") {
      toast.show({
        severity: "success",
        summary: FICHA_SALVA_MESSAGE,
        life: FICHA_SALVA_LIFE,
      });
      onSalva?.();
    }
  }

  const idDaFicha = emGeracao ? geracao.salva?.id : fichaId;
  const anexosDe = idDaFicha ? { obraId, fichaId: idDaFicha } : undefined;

  const anexosPrevia = emPrevia
    ? {
        anexos: geracao.anexos,
        adicionar: geracao.adicionarAnexo,
        remover: geracao.removerAnexo,
        bloqueado: geracao.salvando,
      }
    : undefined;

  return {
    status,
    errorMessage,
    recarregar: reload,
    temDados: ficha !== null && ficha !== undefined,
    isMobile,
    sections,
    menu: sections.map((section) => ({
      id: section.id,
      title: section.title,
      testId: section.menuTestId,
    })),
    activeId,
    mobileAttr: isMobile || undefined,
    bodyRef,
    selecionarSecao,
    propsCorpo,
    acoes:
      podeExportar && !emPrevia
        ? {
            exportando: download.isExporting,
            exportar: abrirExportacao,
            enviarEmail,
          }
        : null,
    exportDialog: {
      isOpen: download.isOpen,
      isExporting: download.isExporting,
      xlsxLayouts: XLSX_LAYOUT_DETAILS,
      exportar: exportarArquivo,
      fechar: download.close,
    },
    emGeracao,
    emPrevia,
    anexosDe,
    anexosPrevia,
    salvando: geracao.salvando,
    salvar,
    cancelar: () => onFechar?.(),
    fechar: () => onFechar?.(),
  };
}
